import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  reportsAPI,
  type ReturnsByItemDto,
  type ReturnsByItemRow,
  type ReturnsByItemDocument,
  type WasteByItemDto,
  type WasteByItemRow,
  type WasteByItemDocument,
} from '@matix/api-client';
import {
  ReportToolbar, Field, DateTimeRange, fmt3, fmtInt, fmtMoney,
  todayFrom, todayTo, rangeParams, rangeLabel, useLabelOf, wallClock,
} from './reportShared';
import { printReport, type ReportPaper } from './reportPrint';
import { ReportSheetBuilder, downloadReportExcel, type ExcelColumn } from './reportExcel';
import { useLang } from '../../lib/language';

/**
 * Returns and Waste (المرتجعات / التوالف) — two reports, each one row per item
 * and unit, so a branch can check how much of each item came back and how
 * much was written off.
 *
 *  - Returns: every line handed back over the counter (Codes 53/54).
 *  - Waste: every Destruction voucher (Code 61) of the branch — the ones the
 *    ERP records and the ones the till writes beside a return whose goods did
 *    not go back on the shelf. The "from returns" column is that second kind,
 *    so the two reports can be reconciled.
 *
 * Each item row opens the documents behind it (returns / vouchers), with the
 * quantity each one carried. The details stay on screen: the printout and the
 * workbook are the per-item summary.
 *
 * The two reports never overlap (the user, 2026-09-27): Returns shows only
 * what went BACK TO STOCK — a return line less what the till wrote off on its
 * Code 61 — and Waste shows every write-off (till or ERP), none of which
 * returned to stock.
 *
 * Money: one Sale Price and one Total Value per item, summed in the footer.
 * Both reports value at SALE price, never cost: the item's list price for the
 * unit (the document price when it has none) × the quantity.
 *
 * Open to every cashier (no report permission), always the till's own branch.
 */

type Col<R> = ExcelColumn & { value: (r: R) => string | number | null; fmt?: (n: number) => string };

interface ByItemProps<R, D extends { rows: R[] }> {
  kind: 'returns' | 'waste';
  title: { en: string; ar: string };
  emptyText: { en: string; ar: string };
  /** Only returns carry a cashier; waste vouchers are mostly entered in the ERP. */
  withCashier: boolean;
  fetch: (p: RangeParams & { cashier_id?: number }) => Promise<D>;
  columns: (labelOf: ReturnType<typeof useLabelOf>) => Array<Col<R>>;
  totals: (d: D) => Array<string | number | null>;
  truncated?: (d: D) => boolean;
  /** The documents behind one item row, shown when the row is opened. */
  details: (r: R) => JSX.Element;
}

type RangeParams = { from: string; to: string; from_time?: string; to_time?: string };

function ByItemReport<R, D extends { rows: R[] }>(props: ByItemProps<R, D>): JSX.Element {
  const { t, lang } = useLang();
  const labelOf = useLabelOf();
  const printRef = useRef<HTMLDivElement>(null);
  const [branchId, setBranchId] = useState<number | ''>('');
  const [cashierId, setCashierId] = useState<number | ''>('');
  const [from, setFrom] = useState(todayFrom);
  const [to, setTo] = useState(todayTo);
  const [params, setParams] = useState<(RangeParams & { cashier_id?: number }) | null>(null);

  const filtersQ = useQuery({ queryKey: ['rpt-filters', null], queryFn: () => reportsAPI.filters() });
  const ownBranchId = filtersQ.data?.default_branch_id ?? null;
  const branches = useMemo(
    () => (filtersQ.data?.branches ?? []).filter((b) => ownBranchId == null || b.id === ownBranchId),
    [filtersQ.data, ownBranchId],
  );
  useEffect(() => {
    if (filtersQ.data && branchId === '') setBranchId(ownBranchId ?? branches[0]?.id ?? '');
  }, [filtersQ.data, branchId, ownBranchId, branches]);

  const dataQ = useQuery({
    queryKey: [`${props.kind}-by-item`, params],
    queryFn: () => props.fetch(params!),
    enabled: params != null,
  });
  const [open, setOpen] = useState<Set<number>>(new Set());
  useEffect(() => setOpen(new Set()), [dataQ.data]);
  const toggle = (i: number): void =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  const range = rangeLabel(from, to);
  const title = t(props.title);
  const branch = filtersQ.data?.branches.find((b) => b.id === branchId);
  const scope = branch ? labelOf(branch) : '';
  const subtitle = scope ? `${scope} · ${range}` : range;

  function run(): void {
    const r = rangeParams(from, to);
    if (!r) return;
    setParams({ ...r, ...(props.withCashier && cashierId !== '' ? { cashier_id: cashierId } : {}) });
  }

  const d = dataQ.data;
  const columns = props.columns(labelOf);
  const totals = d ? props.totals(d) : [];

  function exportPdf(paper: ReportPaper): void {
    // The per-item summary only: opened document rows and the row chevrons
    // are screen furniture, and the roll printout pairs cells by position.
    const copy = printRef.current?.cloneNode(true) as HTMLElement | undefined;
    copy?.querySelectorAll('.rpt-detail, .rpt-chevron, .rpt-toggle-all').forEach((n) => n.remove());
    printReport(title, subtitle, copy?.innerHTML ?? '', paper, { landscape: true });
  }
  function exportExcel(): void {
    if (!d) return;
    const sheet = new ReportSheetBuilder()
      .title(title, subtitle)
      .table(
        columns.map(({ label, kind }) => ({ label, ...(kind ? { kind } : {}) })),
        d.rows.map((r) => columns.map((c) => c.value(r))),
        { emptyText: t(props.emptyText), ...(d.rows.length > 0 ? { totals } : {}) },
      )
      .build(title, { rightToLeft: lang === 'ar' });
    downloadReportExcel(`${title} ${range}`, sheet);
  }

  return (
    <div className="rpt-wrap">
      <ReportToolbar
        running={dataQ.isFetching}
        canExport={!!d}
        onRun={run}
        onExport={exportPdf}
        onExportExcel={exportExcel}
        filters={
          <>
            <Field label={t({ en: 'Branch', ar: 'الفرع' })}>
              <select value={branchId} onChange={(e) => setBranchId(e.target.value ? Number(e.target.value) : '')}>
                {branches.map((b) => <option key={b.id} value={b.id}>{labelOf(b)}</option>)}
              </select>
            </Field>
            {props.withCashier && (
              <Field label={t({ en: 'Cashier', ar: 'الكاشير' })}>
                <select value={cashierId} onChange={(e) => setCashierId(e.target.value ? Number(e.target.value) : '')}>
                  <option value="">{t({ en: '— All —', ar: '— الكل —' })}</option>
                  {filtersQ.data?.cashiers.map((c) => <option key={c.id} value={c.id}>{labelOf(c)}</option>)}
                </select>
              </Field>
            )}
            <DateTimeRange from={from} to={to} onFrom={setFrom} onTo={setTo} />
          </>
        }
      />

      {dataQ.error && <div className="rpt-error">{t({ en: 'Failed to load report. Try again.', ar: 'تعذّر تحميل التقرير. حاول مجدداً.' })}</div>}

      <div className="rpt-print" ref={printRef}>
        {d && (
          <>
            <div className="rpt-meta">{subtitle}</div>
            {props.truncated?.(d) && (
              <div className="rpt-error">
                {t({
                  en: 'The range holds more return lines than one report can read — narrow the dates for complete totals.',
                  ar: 'النطاق يحتوي على أسطر مرتجعات أكثر مما يمكن قراءته في تقرير واحد — ضيّق التواريخ لإجماليات كاملة.',
                })}
              </div>
            )}
            {d.rows.length === 0 ? (
              <div className="rpt-empty">{t(props.emptyText)}</div>
            ) : (
              <div className="rpt-scroll">
                <button
                  type="button"
                  className="rpt-toggle-all"
                  onClick={() => setOpen(open.size === d.rows.length ? new Set() : new Set(d.rows.map((_, i) => i)))}
                >
                  {open.size === d.rows.length
                    ? t({ en: 'Collapse all', ar: 'طي الكل' })
                    : t({ en: 'Expand all', ar: 'توسيع الكل' })}
                </button>
                <table className="rpt-wide">
                  <thead>
                    <tr>
                      {columns.map((c) => (
                        <th key={c.label} className={c.fmt ? 'num' : undefined}>{c.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {d.rows.map((r, i) => (
                      <Fragment key={i}>
                        <tr
                          className={`rpt-expandable${open.has(i) ? ' open' : ''}`}
                          onClick={() => toggle(i)}
                          aria-expanded={open.has(i)}
                        >
                          {columns.map((c, ci) => {
                            const v = c.value(r);
                            return (
                              <td key={c.label} className={c.fmt ? 'num' : undefined}>
                                {ci === 0 && <span className="rpt-chevron" aria-hidden="true">›</span>}
                                {c.fmt && typeof v === 'number' ? c.fmt(v) : v ?? ''}
                              </td>
                            );
                          })}
                        </tr>
                        {open.has(i) && (
                          <tr className="rpt-detail">
                            <td colSpan={columns.length}>{props.details(r)}</td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                  {/* One cell per column, no colSpan: the roll printout pairs
                      each cell with its header by position. */}
                  <tfoot>
                    <tr>
                      {columns.map((c, i) => {
                        const v = totals[i];
                        return (
                          <td key={c.label} className={c.fmt ? 'num' : undefined}>
                            {c.fmt && typeof v === 'number' ? c.fmt(v) : v ?? ''}
                          </td>
                        );
                      })}
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </>
        )}
        {!d && !dataQ.isFetching && <div className="rpt-empty">{t({ en: 'Choose filters, then “Show report”.', ar: 'اختر المرشحات، ثم “عرض التقرير”.' })}</div>}
      </div>
    </div>
  );
}


/** A small table of the documents behind one item row. */
function DocsTable<T>(props: {
  docs: T[];
  columns: Array<{ label: string; value: (d: T) => string | number | null; num?: boolean }>;
}): JSX.Element {
  return (
    <table className="rpt-docs">
      <thead>
        <tr>
          {props.columns.map((c) => <th key={c.label} className={c.num ? 'num' : undefined}>{c.label}</th>)}
        </tr>
      </thead>
      <tbody>
        {props.docs.map((d, i) => (
          <tr key={i}>
            {props.columns.map((c) => <td key={c.label} className={c.num ? 'num' : undefined}>{c.value(d) ?? ''}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Item code, both names and the unit — the leading columns of both reports. */
function itemColumns<R extends ReturnsByItemRow | WasteByItemRow>(
  t: (b: { en: string; ar: string }) => string,
  labelOf: ReturnType<typeof useLabelOf>,
): Array<Col<R>> {
  return [
    { label: t({ en: 'Item Code', ar: 'كود الصنف' }), value: (r) => r.item_code },
    { label: t({ en: 'Item Name (EN)', ar: 'اسم الصنف (إنجليزي)' }), value: (r) => r.item_name },
    { label: t({ en: 'Item Name (AR)', ar: 'اسم الصنف (عربي)' }), value: (r) => r.item_name_ar ?? '' },
    {
      label: t({ en: 'Unit', ar: 'الوحدة' }),
      value: (r) => (r.unit || r.unit_ar ? labelOf({ name: r.unit ?? '', name_ar: r.unit_ar }) : ''),
    },
  ];
}

export function ReturnsByItemReport(): JSX.Element {
  const { t } = useLang();
  const labelOf = useLabelOf();
  const nameOf = (n: string | null, ar: string | null) => (n || ar ? labelOf({ name: n ?? '', name_ar: ar }) : '');
  return (
    <ByItemReport<ReturnsByItemRow, ReturnsByItemDto>
      kind="returns"
      title={{ en: 'Returns Report by Item', ar: 'تقرير المرتجعات بالأصناف' }}
      emptyText={{ en: 'No returns for these filters.', ar: 'لا توجد مرتجعات لهذه المرشحات.' }}
      withCashier
      fetch={(p) => reportsAPI.returnsByItem(p)}
      truncated={(d) => d.truncated}
      details={(r) => (
        <DocsTable<ReturnsByItemDocument>
          docs={r.documents}
          columns={[
            { label: t({ en: 'Return No.', ar: 'رقم المرتجع' }), value: (d) => d.receipt_no },
            { label: t({ en: 'Date', ar: 'التاريخ' }), value: (d) => wallClock(d.entry_date) },
            { label: t({ en: 'Original Invoice', ar: 'الفاتورة الأصلية' }), value: (d) => d.original_no },
            { label: t({ en: 'Qty', ar: 'الكمية' }), value: (d) => fmt3(d.qty), num: true },
            { label: t({ en: 'Cashier', ar: 'الكاشير' }), value: (d) => nameOf(d.cashier_name, d.cashier_name_ar) },
            { label: t({ en: 'Customer', ar: 'العميل' }), value: (d) => nameOf(d.customer_name, d.customer_name_ar) },
            { label: t({ en: 'Notes', ar: 'ملاحظات' }), value: (d) => d.notes },
          ]}
        />
      )}
      columns={(labelOf) => [
        ...itemColumns<ReturnsByItemRow>(t, labelOf),
        { label: t({ en: 'Returns', ar: 'عدد المرتجعات' }), kind: 'qty', value: (r) => r.returns, fmt: fmtInt },
        { label: t({ en: 'Returned to Stock', ar: 'المرتجع للمخزون' }), kind: 'qty', value: (r) => r.qty, fmt: fmt3 },
        { label: t({ en: 'Sale Price', ar: 'سعر البيع' }), kind: 'money', value: (r) => r.sale_price, fmt: fmtMoney },
        { label: t({ en: 'Total Value', ar: 'إجمالي القيمة' }), kind: 'money', value: (r) => r.value, fmt: fmtMoney },
      ]}
      totals={(d) => [
        `${t({ en: 'Total', ar: 'الإجمالي' })} (${fmtInt(d.totals.items)} ${t({ en: 'items', ar: 'صنف' })})`,
        null, null, null,
        d.totals.returns, d.totals.qty, null, d.totals.value,
      ]}
    />
  );
}

export function WasteByItemReport(): JSX.Element {
  const { t } = useLang();
  const labelOf = useLabelOf();
  const nameOf = (n: string | null, ar: string | null) => (n || ar ? labelOf({ name: n ?? '', name_ar: ar }) : '');
  return (
    <ByItemReport<WasteByItemRow, WasteByItemDto>
      kind="waste"
      title={{ en: 'Waste Report by Item', ar: 'تقرير التوالف بالأصناف' }}
      emptyText={{ en: 'No waste for these filters.', ar: 'لا توجد توالف لهذه المرشحات.' }}
      withCashier={false}
      fetch={(p) => reportsAPI.wasteByItem(p)}
      details={(r) => (
        <DocsTable<WasteByItemDocument>
          docs={r.documents}
          columns={[
            { label: t({ en: 'Voucher No.', ar: 'رقم السند' }), value: (d) => d.voucher_no },
            { label: t({ en: 'Date', ar: 'التاريخ' }), value: (d) => wallClock(d.entry_date) },
            { label: t({ en: 'Qty', ar: 'الكمية' }), value: (d) => fmt3(d.qty), num: true },
            {
              label: t({ en: 'From Return No.', ar: 'من المرتجع رقم' }),
              value: (d) => d.return_no ?? t({ en: '— (entered in ERP)', ar: '— (مسجل في النظام)' }),
            },
            { label: t({ en: 'Entered by', ar: 'بواسطة' }), value: (d) => nameOf(d.user_name, d.user_name_ar) },
            { label: t({ en: 'Notes', ar: 'ملاحظات' }), value: (d) => d.notes },
          ]}
        />
      )}
      columns={(labelOf) => [
        ...itemColumns<WasteByItemRow>(t, labelOf),
        { label: t({ en: 'Vouchers', ar: 'عدد السندات' }), kind: 'qty', value: (r) => r.vouchers, fmt: fmtInt },
        { label: t({ en: 'Wasted Qty', ar: 'كمية التوالف' }), kind: 'qty', value: (r) => r.qty, fmt: fmt3 },
        { label: t({ en: 'Of which from Returns', ar: 'منها من المرتجعات' }), kind: 'qty', value: (r) => r.qty_from_returns, fmt: fmt3 },
        { label: t({ en: 'Sale Price', ar: 'سعر البيع' }), kind: 'money', value: (r) => r.sale_price, fmt: fmtMoney },
        { label: t({ en: 'Total Value', ar: 'إجمالي القيمة' }), kind: 'money', value: (r) => r.value, fmt: fmtMoney },
      ]}
      totals={(d) => [
        `${t({ en: 'Total', ar: 'الإجمالي' })} (${fmtInt(d.totals.items)} ${t({ en: 'items', ar: 'صنف' })} · ${fmtInt(d.totals.vouchers)} ${t({ en: 'vouchers', ar: 'سند' })})`,
        null, null, null,
        null, d.totals.qty, d.totals.qty_from_returns, null, d.totals.value,
      ]}
    />
  );
}
